/** Discriminator for which visitor info disclaimer a route requires. */
export type VisitorInfoType = 'none' | 'funds' | 'publications';

/** Maps a visitorInfoType to its sessionStorage key. */
export const STORAGE_KEYS: Record<Exclude<VisitorInfoType, 'none'>, string> = {
  funds: 'visitorInfo',
  publications: 'publicationsVisitorInfo',
};

/** Returns the sessionStorage key for a given visitor info type, or null for 'none'. */
export function getStorageKey(type: VisitorInfoType): string | null {
  if (type === 'none') return null;
  return STORAGE_KEYS[type];
}

/**
 * Matches a single path segment that is an Austrian fund ISIN: the "AT" country
 * code, followed by 9 alphanumeric characters and a final numeric check digit
 * (12 characters total, e.g. "AT0000A1TVM9").
 *
 * Anchored to the "AT" country code on purpose: the legacy (React Router v3)
 * route was `AT**(/archive)`, i.e. only Austrian ISIN URLs were treated as fund
 * pages requiring the visitor-info disclaimer. Keeping the same scope preserves
 * the original behavior. If other country codes ever need direct links, widen
 * the country part to `[A-Z]{2}`.
 */
const AT_ISIN_SEGMENT = /^AT[0-9A-Z]{9}[0-9]$/;

/** Returns true if a single path segment is an Austrian fund ISIN. */
export function isIsinSegment(segment: string): boolean {
  return AT_ISIN_SEGMENT.test(segment);
}

/**
 * Language prefixes used by the app's routes (see src/routing/index.tsx). Kept in
 * sync with the `<Route path="de|en|hu|cs|ru">` language groups. Used to skip an
 * optional leading language segment when detecting a bare ISIN fund URL.
 */
const LANGUAGE_SEGMENTS = new Set(['de', 'en', 'hu', 'cs', 'ru']);

/**
 * Returns true only when the pathname points *directly* at a fund by ISIN — i.e.
 * the first path segment (after an optional language prefix) is an ISIN.
 *
 * This mirrors exactly what the legacy `AT**(/archive)` route matched: a bare
 * ISIN (`/AT0000A1TVM9`), a language-prefixed ISIN (`/en/AT0000A1TVM9`), and the
 * archive sub-page (`/AT0000A1TVM9/archive`).
 *
 * It deliberately does NOT match prefixed fund routes such as `/gd/AT0000A1E853`
 * (public "guest direct" pages, which have their own `none` route and must stay
 * disclaimer-free) or `/gf/AT...`. Only the leading ISIN position is treated as
 * the disclaimer-gated bare-ISIN route.
 */
export function isBareIsinFundPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  // Skip an optional leading language prefix (e.g. /en/AT0000A1TVM9).
  const fundSegmentIndex = segments.length > 0 && LANGUAGE_SEGMENTS.has(segments[0]) ? 1 : 0;
  const fundSegment = segments[fundSegmentIndex];
  return fundSegment !== undefined && isIsinSegment(fundSegment);
}

/**
 * Resolves the effective visitor-info type for a route.
 *
 * Routes declare a static type, but a bare ISIN URL (e.g. `/AT0000A1TVM9`)
 * cannot be matched by a dedicated route under React Router v6 — a trailing `*`
 * splat only matches after a `/`, so `AT*` is treated as `AT/*` and a bare ISIN
 * falls through to the generic `:path` catch-all, which is typed `'none'`. That
 * would skip the disclaimer gate and let the server's DeniedPage render directly.
 *
 * Upgrading bare ISIN paths to `'funds'` restores the legacy behavior: the
 * disclaimer gate runs and the fund query carries the visitor info. Only `'none'`
 * is ever upgraded, and only for a *bare* ISIN (see isBareIsinFundPath) — prefixed
 * routes like `/gd/AT...` (public) and explicit `'funds'`/`'publications'` routes
 * are returned unchanged.
 */
export function getEffectiveVisitorInfoType(pathname: string, declaredType: VisitorInfoType): VisitorInfoType {
  if (declaredType === 'none' && isBareIsinFundPath(pathname)) {
    return 'funds';
  }
  return declaredType;
}
